import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useIdleTimeout } from '../../context/useIdleTimeout';
import { isEntraAuth, msalInstance } from '../../auth/authConfig';
import { databasesApi, kpiApi } from '../../services/api';
import { setTabDatabase } from '../../services/activeDatabase';
import splashVideo from '../../assets/dawn_splash_2.mp4';
import wmLogo from '../../assets/logos/WM-Logo-CB-White-Header.png';
import { CheckCircle, Bug, LogOut, Loader2 } from 'lucide-react';

const ALL_STEPS = [
  { key: 'connecting',     label: 'Connecting to Microsoft' },
  { key: 'authenticating', label: 'Signing you in'          },
  { key: 'verifying',      label: 'Checking your account'   },
  { key: 'selecting',      label: 'Select a database'       },
  { key: 'loading',        label: 'Loading your workspace'  },
];
const STEP_ORDER = ALL_STEPS.map(s => s.key);

const AuthGate = ({ children }) => {
  const location   = useLocation();
  const navigate   = useNavigate();
  const { authStep, currentUser, accessDenied, isSysAdmin } = useAuth();

  // Pen test R2 Low finding — auto-logout on inactivity. Non-SysAdmin
  // hard cap = 120 min (2h). SysAdmin uncapped. Preference lives in
  // localStorage `pmd-security.sessionTimeout` (PreferencesPage).
  useIdleTimeout(msalInstance);

  const isInvitePage  = location.pathname.startsWith('/accept-invitation');
  const alreadyShown  = sessionStorage.getItem('dawn-splash-shown') === 'true';
  const noAccountsYet = isEntraAuth() && msalInstance && msalInstance.getAllAccounts().length === 0;
  const skipVideo     = isInvitePage || alreadyShown || noAccountsYet;

  const [videoPhase,  setVideoPhase]  = useState(!skipVideo);
  const [localPhase,  setLocalPhase]  = useState('auth'); // 'auth' | 'selecting' | 'loading'
  const [databases,   setDatabases]   = useState([]);
  // Distinct from databases.length === 0. The list starts empty BEFORE the fetch
  // runs, so length alone would show the no-access message to every user during
  // a normal load. Only a settled fetch can prove the user truly has none.
  const [dbFetchSettled, setDbFetchSettled] = useState(false);
  // A failed fetch is NOT the same as an empty one. Treating them alike would
  // tell someone to call their administrator about a permissions problem they
  // do not have, when the call simply errored.
  const [dbFetchFailed,  setDbFetchFailed]  = useState(false);
  const [gateExiting, setGateExiting] = useState(false);
  const [gateDone,    setGateDone]    = useState(false);
  const [debugMode,   setDebugMode]   = useState(false);
  const [, forceUpdate] = useState(0);

  const startTimeRef  = useRef(Date.now());
  const logRef        = useRef([]);
  const debugModeRef  = useRef(false);

  const addLog = useCallback((msg, type = 'info') => {
    const ms      = Date.now() - startTimeRef.current;
    const sec     = Math.floor(ms / 1000);
    const frac    = String(ms % 1000).padStart(3, '0');
    const elapsed = `[${String(sec).padStart(2, '0')}:${frac}]`;
    logRef.current = [...logRef.current, { time: elapsed, msg, type }];
    if (debugModeRef.current) forceUpdate(n => n + 1);
  }, []);

  const toggleDebug = () => {
    const next = !debugMode;
    debugModeRef.current = next;
    setDebugMode(next);
    if (next) forceUpdate(n => n + 1);
  };

  const exitGate = useCallback(() => {
    addLog('Gate complete', 'step');
    setGateExiting(true);
    setTimeout(() => setGateDone(true), 500);
  }, [addLog]);

  // Log authStep transitions
  const prevAuthStep = useRef(null);
  useEffect(() => {
    if (authStep !== prevAuthStep.current && !gateDone) {
      addLog(`AUTH: ${authStep}`, 'step');
      prevAuthStep.current = authStep;
    }
  }, [authStep, addLog, gateDone]);

  // --- Video phase ---
  const completeVideo = useCallback(() => {
    sessionStorage.setItem('dawn-splash-shown', 'true');
    setVideoPhase(false);
  }, []);

  useEffect(() => {
    if (!videoPhase) return;
    const t = setTimeout(completeVideo, 60000);
    return () => clearTimeout(t);
  }, [videoPhase, completeVideo]);

  // --- Transition: auth ready → selecting ---
  useEffect(() => {
    if (!videoPhase && authStep === 'ready' && currentUser && localPhase === 'auth' && !gateExiting) {
      addLog('Auth resolved → selecting database', 'step');
      setLocalPhase('selecting');
    }
  }, [videoPhase, authStep, currentUser, localPhase, gateExiting, addLog]);

  // --- Step 4: fetch DB list ---
  const handleSelectDb = useCallback((db) => {
    // URL slug now comes from db.slug (the Slug column). Falls back to display-name-lowercased
    // for old DBs that don't have a slug populated yet (shouldn't happen post-migration).
    const slug = db.slug || (db.displayName || db.name).toLowerCase();
    const name = db.databaseName || db.displayName || db.name;
    setTabDatabase({ databaseName: name, slug, clientName: db.organizationName || db.displayName || '' });
    // Preserve a deep link. Pasting /gfs01/drawings?view=datasheet into a cold tab
    // lands here after sign-in; navigating to the DB root would throw the path and
    // query away, which made shared links useless (DevTrack 114). Only keep the
    // requested URL when it targets the DB actually being selected.
    const firstSeg = (location.pathname.split('/').filter(Boolean)[0] || '').toLowerCase();
    const target = firstSeg === slug
      ? `${location.pathname}${location.search}`
      : `/${slug}/`;
    addLog(`DB selected: ${db.displayName || db.name} → ${target}`, 'step');
    navigate(target);
    setLocalPhase('loading');
  }, [addLog, navigate, location.pathname, location.search]);

  useEffect(() => {
    if (localPhase !== 'selecting') return;
    addLog('API: databases/available → started');
    const t0 = Date.now();
    databasesApi.getAvailable()
      .then(res => {
        const dbs = res.data || [];
        addLog(`API: databases/available → OK (${Date.now() - t0}ms), ${dbs.length} DB(s)`);
        setDatabases(dbs);
        setDbFetchSettled(true);
        if (dbs.length === 1) handleSelectDb(dbs[0]);
      })
      .catch(err => {
        addLog(`API: databases/available → ERROR: ${err.message}`, 'error');
        setDatabases([]);
        setDbFetchFailed(true);
        setDbFetchSettled(true);
      });
  }, [localPhase, handleSelectDb, addLog]);

  // --- Step 5: KPI pre-fetch (soft gate, 8s timeout) ---
  useEffect(() => {
    if (localPhase !== 'loading') return;
    addLog('API: kpi/home → started');
    const t0 = Date.now();
    let settled = false;
    const settle = (msg, type = 'info') => {
      if (settled) return;
      settled = true;
      addLog(msg, type);
      exitGate();
    };
    const timeout = setTimeout(
      () => settle('API: kpi/home → 8s timeout (soft gate — proceeding)', 'error'),
      8000
    );
    kpiApi.getHome()
      .then(() => { clearTimeout(timeout); settle(`API: kpi/home → OK (${Date.now() - t0}ms)`); })
      .catch(err => { clearTimeout(timeout); settle(`API: kpi/home → ERROR: ${err.message}`, 'error'); });
    return () => clearTimeout(timeout);
  }, [localPhase, exitGate, addLog]);

  // Combined display step for step indicators
  const displayStep    = localPhase === 'selecting' ? 'selecting'
    : localPhase === 'loading' ? 'loading'
    : (authStep === 'ready' ? 'verifying' : authStep);
  const currentStepIdx = STEP_ORDER.indexOf(displayStep);

  if (isInvitePage) return children;
  if (gateDone)     return children;

  const showDbList = localPhase === 'selecting' && databases.length > 1;

  // ── Dead ends ──────────────────────────────────────────────────────────
  // The gate used to have no branch for either of these, so both spun forever:
  // auto-select needs exactly 1 database and the picker needs more than 1, so
  // zero matched neither, and a null currentUser never satisfied the transition
  // out of the auth phase at all. App.js has the right handling for both but it
  // lives inside this gate, so it could never run.
  const noDatabases   = localPhase === 'selecting' && dbFetchSettled && !dbFetchFailed && databases.length === 0;
  const dbLoadFailed  = localPhase === 'selecting' && dbFetchFailed;
  const unverified    = !videoPhase && authStep === 'ready' && !currentUser && !accessDenied;
  const deadEnd       = noDatabases || dbLoadFailed || unverified;

  // Debug trace: normally for holders of preferences.debug, scanning every
  // permission bucket because no database is selected at the gate (same shape as
  // PreferencesPage). Also shown at a dead end regardless: the trace exists to
  // diagnose exactly the states where permissions cannot resolve, and at a dead
  // end the log holds no database names - there are none, or auth never resolved.
  const hasDebugPerm  = isSysAdmin || Object.values(currentUser?.permissions || {})
                          .some(list => (list || []).some(k => k === 'preferences.debug'));
  const showDebug     = hasDebugPerm || deadEnd;

  const signOut = () => {
    if (isEntraAuth() && msalInstance) msalInstance.logoutRedirect();
    else window.location.reload();
  };

  return (
    <>
      {gateExiting && children}
      <div className={`stui-auth-gate-backdrop${gateExiting ? ' stui-auth-gate-exiting' : ''}`}>
        <div className="stui-auth-gate-card" style={{ position: 'relative' }}>

          {videoPhase ? (
            <video
              className="stui-auth-gate-video"
              autoPlay muted playsInline
              onCanPlay={e => { e.target.playbackRate = 2; }}
              onEnded={completeVideo}
            >
              <source src={splashVideo} type="video/mp4" />
            </video>
          ) : (
            <>
              {/* Header */}
              <div className="stui-auth-gate-header">
                <img src={wmLogo} alt="Wunderlich-Malec" className="stui-auth-gate-logo" />
                <div className="stui-auth-gate-header-divider" />
                <div>
                  <div className="stui-auth-gate-title">DAWN</div>
                  <div className="stui-auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKING</div>
                </div>
              </div>

              {/* Debug toggle — top right, where an X close would sit (this card has no X).
                   Gated on preferences.debug, always available at a dead end. */}
              {showDebug && (
                <button
                  className={`stui-debug-btn stui-auth-gate-debug-icon${debugMode ? ' active' : ''}`}
                  onClick={toggleDebug}
                  title={debugMode ? 'Hide debug trace' : 'Show debug trace'}
                >
                  <Bug size={13} />
                </button>
              )}

              {/* Body — dead ends win outright, then the picker, then the step indicators.
                   The debug trace is an OVERLAY on all three rather than a fourth branch: as a
                   branch it did nothing while the database picker was showing, which made the
                   toggle look broken. */}
              <div className="stui-auth-gate-body">
              {deadEnd ? (
                <div className="stui-auth-gate-deadend">
                  <div className="stui-auth-gate-deadend-title">
                    {noDatabases  ? 'No database access'
                      : dbLoadFailed ? 'Could not load your databases'
                      : 'Account not verified'}
                  </div>
                  <div className="stui-auth-gate-deadend-text">
                    {noDatabases
                      ? 'You have not been granted access to any databases. Please contact your administrator.'
                      : dbLoadFailed
                        ? 'Something went wrong loading your databases. Please refresh to try again — if it keeps happening, contact your administrator.'
                        : 'We could not verify your account. Please refresh to try again — if it keeps happening, contact your administrator.'}
                  </div>
                  <button className="stui-auth-gate-deadend-action" onClick={signOut}>
                    <LogOut size={14} />
                    <span>Sign out</span>
                  </button>
                </div>
              ) : showDbList ? (
                <>
                  <div className="stui-auth-gate-db-header">Select a database</div>
                  <div className="stui-auth-gate-db-list">
                    {databases.map(db => {
                      const name = db.displayName || db.name;
                      return (
                        <button key={db.id || name} className="stui-auth-gate-db-item" onClick={() => handleSelectDb(db)}>
                          {db.iconUrl ? (
                            <img src={db.iconUrl} alt="" className="stui-auth-gate-db-icon" />
                          ) : (
                            <div className="stui-auth-gate-db-icon-placeholder">{name.charAt(0)}</div>
                          )}
                          <div className="stui-auth-gate-db-info">
                            <div className="stui-auth-gate-db-name">{name}</div>
                            {db.description && <div className="stui-auth-gate-db-desc">{db.description}</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="stui-auth-gate-steps">
                  {ALL_STEPS.map((step, idx) => {
                    const isDone   = idx < currentStepIdx;
                    const isActive = !isDone && idx === currentStepIdx;
                    const state    = isDone ? 'done' : isActive ? 'active' : 'pending';
                    return (
                      <div key={step.key} className={`stui-auth-gate-step ${state}`}>
                        <div className="stui-auth-gate-step-icon">
                          {isDone   ? <CheckCircle size={20} color="#1a5568" /> :
                           isActive ? <Loader2 size={20} className="stui-spinner" /> :
                                      <div className="stui-auth-gate-step-circle" />}
                        </div>
                        <span className="stui-auth-gate-step-label">{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {debugMode && showDebug && (
                <div className="stui-auth-gate-debug-panel">
                  {logRef.current.length === 0 ? (
                    <div className="stui-auth-gate-debug-entry empty">No events yet</div>
                  ) : (
                    logRef.current.map((entry, i) => (
                      <div
                        key={i}
                        className={`stui-auth-gate-debug-entry${entry.type === 'error' ? ' error' : entry.type === 'step' ? ' step' : ''}`}
                      >
                        {entry.time} {entry.msg}
                      </div>
                    ))
                  )}
                </div>
              )}
              </div>
            </>
          )}

        </div>
      </div>
    </>
  );
};

export default AuthGate;
